import { defineStore } from 'pinia';
import { z } from 'zod';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { StoreNames } from '../../../shared/store-names';
import { useDataTableSettings } from '../../../composables';
import { useUiStateAutoPersist, useUiStatesApi } from '../../ui-states';
import {
  selectFilterValidationSchema,
  multiSelectFilterValidationSchema,
  rangeFilterValidationSchema,
} from '../../filters';
import { InventoriesColumns as Columns } from '../shared/inventories-columns';
import type { ReplenishmentInventoriesFilterState } from '../interfaces';

const UI_STATES_KEY = StoreNames.ReplenishmentInventoriesPage;

export const useReplenishmentInventoriesPageStore = defineStore(UI_STATES_KEY, () => {
  const defaultAppliedFilters: ReplenishmentInventoriesFilterState = {
    location_id: ['in', []],
    sku_id: ['in', []],
    product_id: ['in', []],
    categories: ['in', []],
    brands: ['in', []],
    seasons: ['in', []],
    styles: ['in', []],
    department_id: ['in', []],
    replenishment: ['in_range', [null, null]],
    optimal_stock: ['in_range', [null, null]],
    optimal_stock_variance: ['in_range', [null, null]],
    constraints: ['in', []],
    optimal_stock_variance_percentage: ['in_range', [null, null]],
    special_event_names: ['in', []],
    days_since_allocation: ['in_range', [null, null]],
    site_qty: ['in_range', [null, null]],
    last_updated: ['eq', null],
  };
  const appliedFilters = ref<ReplenishmentInventoriesFilterState>(cloneDeep(defaultAppliedFilters));

  const filtersValidationSchema = z.object({
    location_id: multiSelectFilterValidationSchema,
    sku_id: multiSelectFilterValidationSchema,
    product_id: multiSelectFilterValidationSchema,
    categories: multiSelectFilterValidationSchema,
    brands: multiSelectFilterValidationSchema,
    seasons: multiSelectFilterValidationSchema,
    styles: multiSelectFilterValidationSchema,
    department_id: multiSelectFilterValidationSchema,
    replenishment: rangeFilterValidationSchema,
    optimal_stock: rangeFilterValidationSchema,
    optimal_stock_variance: rangeFilterValidationSchema,
    constraints: multiSelectFilterValidationSchema,
    optimal_stock_variance_percentage: rangeFilterValidationSchema,
    special_event_names: multiSelectFilterValidationSchema,
    days_since_allocation: rangeFilterValidationSchema,
    site_qty: rangeFilterValidationSchema,
    last_updated: selectFilterValidationSchema,
  });

  const quickFilters = ref<string[]>([]);

  const {
    columnsVisibility,
    columnsWidth,
    columnsOrder,
    visibleColumns,
    resetColumnsSettings,
    applyPersistedState,
    columnsSettingsTouched,
    pinnedColumns,
    sorting,
    resetSorting,
    handleColumnResize,
    userColumnsWidth,
  } = useDataTableSettings({
    defaultColumnsVisibility: {
      [Columns.SkuName]: true,
      [Columns.SkuExternalId]: false,
      [Columns.Location]: true,
      [Columns.LocationExternalId]: false,
      [Columns.SkuDescription]: false,
      [Columns.Product]: false,
      [Columns.ProductExternalId]: false,
      [Columns.Style]: false,
      [Columns.Size]: false,
      [Columns.Color]: false,
      [Columns.Category]: false,
      [Columns.AG]: false,
      [Columns.Department]: false,
      [Columns.DepartmentId]: false,
      [Columns.UnitPrice]: false,
      [Columns.Brand]: false,
      [Columns.Season]: false,
      [Columns.TargetMarket]: false,
      [Columns.Replenishment]: true,
      [Columns.ReplenishmentTime]: false,
      [Columns.ShipmentDays]: false,
      [Columns.OptimalStock]: true,
      [Columns.OptimalStockVariance]: false,
      [Columns.OptimalStockVariancePercentage]: false,
      [Columns.InventoryNeeded]: true,
      [Columns.Stock]: true,
      [Columns.WhInventory]: true,
      [Columns.Sold]: false,
      [Columns.SaleRate]: true,
      [Columns.Coverage]: false,
      [Columns.ReplenishmentGap]: true,
      [Columns.Constraints]: true,
      [Columns.SpecialEvent]: false,
      [Columns.DaysSinceAllocation]: false,
      [Columns.SiteQty]: false,
      [Columns.MasterSkuName]: true,
      [Columns.MasterSkuExternalId]: false,
      [Columns.LastUpdated]: true,
    },
    defaultColumnsWidth: {
      [Columns.Location]: 168,
      [Columns.LocationExternalId]: 256,
      [Columns.SkuName]: 168,
      [Columns.SkuExternalId]: 256,
      [Columns.SkuDescription]: 256,
      [Columns.Product]: 216,
      [Columns.ProductExternalId]: 216,
      [Columns.Style]: 216,
      [Columns.Size]: 196,
      [Columns.Color]: 216,
      [Columns.Category]: 216,
      [Columns.AG]: 216,
      [Columns.Department]: 168,
      [Columns.DepartmentId]: 256,
      [Columns.UnitPrice]: 216,
      [Columns.Brand]: 216,
      [Columns.Season]: 216,
      [Columns.TargetMarket]: 216,
      [Columns.Replenishment]: 168,
      [Columns.ReplenishmentTime]: 216,
      [Columns.ShipmentDays]: 216,
      [Columns.OptimalStock]: 168,
      [Columns.OptimalStockVariance]: 236,
      [Columns.OptimalStockVariancePercentage]: 296,
      [Columns.InventoryNeeded]: 216,
      [Columns.Stock]: 168,
      [Columns.WhInventory]: 168,
      [Columns.Sold]: 168,
      [Columns.SaleRate]: 168,
      [Columns.Coverage]: 168,
      [Columns.ReplenishmentGap]: 236,
      [Columns.Constraints]: 192,
      [Columns.SpecialEvent]: 168,
      [Columns.DaysSinceAllocation]: 236,
      [Columns.LastUpdated]: 168,
      [Columns.SiteQty]: 168,
      [Columns.MasterSkuName]: 256,
      [Columns.MasterSkuExternalId]: 256,
    },
    defaultColumnsOrder: [
      Columns.SkuDescription,
      Columns.Product,
      Columns.ProductExternalId,
      Columns.Style,
      Columns.Size,
      Columns.Color,
      Columns.Category,
      Columns.AG,
      Columns.Department,
      Columns.DepartmentId,
      Columns.MasterSkuName,
      Columns.MasterSkuExternalId,
      Columns.UnitPrice,
      Columns.Brand,
      Columns.Season,
      Columns.TargetMarket,
      Columns.Replenishment,
      Columns.ReplenishmentTime,
      Columns.ShipmentDays,
      Columns.OptimalStock,
      Columns.OptimalStockVariance,
      Columns.OptimalStockVariancePercentage,
      Columns.InventoryNeeded,
      Columns.Stock,
      Columns.SiteQty,
      Columns.WhInventory,
      Columns.Sold,
      Columns.SaleRate,
      Columns.Coverage,
      Columns.ReplenishmentGap,
      Columns.Constraints,
      Columns.SpecialEvent,
      Columns.DaysSinceAllocation,
      Columns.LastUpdated,
    ],
    defaultPinnedColumns: [
      Columns.Location,
      Columns.LocationExternalId,
      Columns.SkuName,
      Columns.SkuExternalId,
    ],
  });

  function reset() {
    resetColumnsSettings();
    resetSorting();
    appliedFilters.value = cloneDeep(defaultAppliedFilters);
  }

  const api = useUiStatesApi();

  const fetching = ref(false);
  const fetched = ref(false);

  let fetchingPromise: Promise<void> | null = null;

  async function fetch(force = false) {
    if (fetched.value && !force) {
      return fetchingPromise ?? Promise.resolve();
    }

    if (!fetchingPromise) {
      fetchingPromise = (async () => {
        fetching.value = true;

        const persistedState = await api
          .getUiStates({ key: UI_STATES_KEY })
          .then(({ data }) => data.data[0]);

        reset();

        // Apply persisted state

        if (persistedState) {
          applyPersistedState(persistedState.value);

          if (persistedState.value.appliedFilters) {
            try {
              appliedFilters.value = filtersValidationSchema.parse(
                persistedState.value.appliedFilters,
              );
            } catch (error) {
              // do nothing
            }
          }

          if (persistedState.value.quickFilters) {
            quickFilters.value = persistedState.value.quickFilters;
          }
        }

        fetching.value = false;
        fetched.value = true;
        fetchingPromise = null;
      })();
    }

    return fetchingPromise;
  }

  const persistingValue = computed(() => ({
    columnsVisibility: { ...columnsVisibility.value },
    columnsWidth: { ...userColumnsWidth.value },
    columnsOrder: [...columnsOrder.value],
    pinnedColumns: [...pinnedColumns.value],
    sorting: { ...sorting.value },
    appliedFilters: { ...appliedFilters.value },
    quickFilters: [...quickFilters.value],
  }));

  useUiStateAutoPersist({
    enabled: fetched,
    value: persistingValue,
    key: UI_STATES_KEY,
  });

  return {
    fetching,
    fetched,
    fetch,
    reset,
    resetColumnsSettings,
    columnsVisibility,
    columnsWidth,
    visibleColumns,
    columnsOrder,
    pinnedColumns,
    sorting,
    appliedFilters,
    columnsSettingsTouched,
    handleColumnResize,
    resetSorting,
    quickFilters,
  };
});
